import { useEffect, useState } from 'react';
import { useNoticeToast } from '../lib/toast';
import { api } from '../lib/api';
import { ErrorBox, dash, useList, type ListResultLike } from '../components/DataTable';
import { useMe } from '../lib/me';
import { CloseDeadlineRow } from '../components/CloseDeadlineRow';

/**
 * 월 마감.
 *
 * ⚠️ **마감은 배차를 실제로 잠근다.** 그 달의 배차는 등록·수정이 거부된다(레거시 게이트 이식).
 *    그래서 걸기 전에 **무엇이 잠기는지 숫자로 보여주고**, 푸는 것도 같은 자리에서 하게 한다 —
 *    풀 방법이 없으면 잘못 건 마감이 운영을 막는다.
 *
 * ⚠️ 레거시는 `@IsAdmin=1` 이면 이 게이트를 **전부 우회**한다. 웹에는 그 우회로가 없다 —
 *    마감을 넘겨야 하면 마감을 푸는 게 올바른 경로다.
 *
 * 🔴 걸기·풀기는 **관리자(=슈퍼관리자)만**(2026-10-01 way). 서버 `setResultFinish` 가 역할로 막고,
 *    여기 버튼은 그 판정을 따라 숨길 뿐이다.
 *
 * 자동 마감(2026-10-15 way): 축마다 「매월 N일」 → 그날 새벽 전월을 건다(`resultFinishAuto.ts`).
 * 칩 끝 이름은 **마지막으로 걸거나 푼 사람**(권한 표시가 아니다) — 자동이면 「자동」.
 */

const TYPES = ['거래처', '기사', '월정기정산'];

export default function SettleClose({ driveMon }: { driveMon: string }) {
  const { me } = useMe();
  const canClose = me?.role === 'admin';
  const [nonce, setNonce] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  useNoticeToast(notice, setNotice);

  const [auto, setAuto] = useState<{ autoFrom: string; rows: { type: string; day: number | null; due: string | null }[] } | null>(null);
  const [editDays, setEditDays] = useState<Record<string, string> | null>(null);
  useEffect(() => {
    if (!driveMon) return;
    api.listResultFinishAuto({ driveMon }).then(setAuto).catch(() => setAuto(null));
  }, [driveMon, nonce]);
  const autoOf = (type: string) => auto?.rows.find((r) => r.type === type);

  async function saveDays() {
    if (!editDays) return;
    setBusy('days');
    setError(null);
    try {
      for (const t of TYPES) {
        const raw = (editDays[t] ?? '').trim();
        const day = raw === '' ? null : Number(raw);
        if (day === (autoOf(t)?.day ?? null)) continue;
        await api.setResultFinishDay({ resultType: t, day });
      }
      setEditDays(null);
      setNotice('자동 마감일을 저장했습니다.');
      setNonce((n) => n + 1);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  const { rows, loading } = useList(
    () =>
      api.listResultFinish({ driveMon: driveMon || undefined }) as unknown as Promise<ListResultLike>,
    [driveMon, nonce],
  );

  const stateOf = (type: string) =>
    rows.find((r) => r.result_type === type && r.drive_mon === driveMon);

  async function toggle(type: string, next: boolean) {
    setBusy(type);
    setError(null);
    setNotice(null);
    try {
      const r = await api.setResultFinish({ driveMon, resultType: type, isFinish: next });
      setNotice(
        next
          ? `${driveMon} ${type} 정산을 마감했습니다. 배차 ${r.lockedDispts}건(접수 ${r.lockedRecvs}건)이 잠깁니다.`
          : `${driveMon} ${type} 정산 마감을 풀었습니다. 배차를 다시 고칠 수 있습니다.`,
      );
      setNonce((n) => n + 1);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  if (!driveMon) {
    return <p className="py-1 text-xs text-ink-faint">정산월을 고르면 마감 상태가 보입니다.</p>;
  }

  return (
    // 한 줄 띠(2026-09-30 way 「위칸을 최대로 좁히고」) — 설명은 제목 툴팁으로 옮겼다. 알림·오류만 줄을 늘린다.
    <div className="card space-y-1 px-3 py-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="cursor-help text-xs font-bold" title="마감하면 그 달 배차의 등록·수정이 막힙니다. 우회 경로는 없습니다.">
          {driveMon} 마감
        </h2>

        {TYPES.map((t) => {
          const st = stateOf(t);
          const on = st?.is_finish === true;
          return (
            <div
              key={t}
              className={`flex items-center gap-2 rounded-lg border px-2 py-0.5 text-xs ${
                on ? 'border-amber-300 bg-amber-50' : 'border-line bg-surface'
              }`}
            >
              <span className="font-medium">{t}</span>
              <span className={on ? 'text-amber-800' : 'text-ink-faint'}>{on ? '마감됨' : '열림'}</span>
              {canClose && (
                <button
                  className="btn-ghost px-2 py-0.5 text-xs"
                  disabled={busy === t || loading}
                  onClick={() => toggle(t, !on)}
                >
                  {busy === t ? '…' : on ? '마감 풀기' : '마감하기'}
                </button>
              )}
              {st?.updated_by ? (
                <span
                  className="max-w-[9rem] truncate text-[11px] text-ink-faint"
                  title={`마지막으로 ${on ? '마감한' : '마감을 푼'} 사람: ${dash(st.updated_by)} · ${dash(st.updated_at).slice(0, 16)}`}
                >
                  {on ? '마감' : '풂'} · {st.updated_by === 'job' ? '자동' : dash(st.updated_by).split('@')[0]}
                </span>
              ) : null}
              {(() => {
                const a = autoOf(t);
                if (!a || a.day === null) return <span className="text-[11px] text-ink-faint">자동 꺼짐</span>;
                return (
                  <span
                    className="text-[11px] text-emerald-700"
                    title={a.due ? `${a.due} 새벽에 자동으로 마감합니다(그 전에 직접 걸거나 푼 상태는 따릅니다).` : `${auto?.autoFrom} 정산월부터 자동 마감합니다.`}
                  >
                    매월 {a.day}일 자동{!on && a.due ? ` (${a.due.slice(5)})` : ''}
                  </span>
                );
              })()}
            </div>
          );
        })}
        {canClose && !editDays && (
          <button
            className="btn-ghost px-2 py-0.5 text-xs"
            onClick={() => setEditDays(Object.fromEntries(TYPES.map((t) => [t, String(autoOf(t)?.day ?? '')])))}
          >
            자동 마감일
          </button>
        )}
        {!canClose && <span className="text-[11px] text-ink-faint">마감은 관리자만</span>}
      </div>

      {editDays && (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="text-ink-soft">매월 며칠에 전월을 자동 마감할까요? (비우면 그 구분은 자동 안 함)</span>
          {TYPES.map((t) => (
            <label key={t} className="flex items-center gap-1">
              {t}
              <input
                className="field w-14 px-1 py-0.5 text-xs"
                inputMode="numeric"
                value={editDays[t] ?? ''}
                onChange={(e) => setEditDays({ ...editDays, [t]: e.target.value.replace(/[^0-9]/g, '').slice(0, 2) })}
              />
              일
            </label>
          ))}
          <button className="btn-primary px-2 py-0.5 text-xs" disabled={busy === 'days'} onClick={saveDays}>
            {busy === 'days' ? '…' : '저장'}
          </button>
          <button className="btn-ghost px-2 py-0.5 text-xs" onClick={() => setEditDays(null)}>
            취소
          </button>
        </div>
      )}

      <CloseDeadlineRow driveMon={driveMon} />

      {error && <ErrorBox message={error} />}
    </div>
  );
}
